import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import {
  getDigitalProducts,
  getMoreDigitalProducts,
} from '../../../Redux/digital-product/slice';
import { PublicFilterDigitalProduct } from '../../../Redux/digital-product/types';
import NavbarTwo from '../../globalcomponents/navbar/NavbarTwo';
import FilterDetails from '../mobilecategories/minifilters/FilterDetails';
import Filters from '../mobilecategories/minifilters/Filters';
import DigitalProductCategories from './DIgitalproductCategories';
import DigitalProductFilter from './DigitalproductFilter';
import DigitalProducts from './DigitalProducts';
import ProDetailModal from '../homepage/components/ProDetailModal';
import { Product } from '../../../Redux/ecommersProduct/types';

type TabType = 'all' | 'digital' | 'service' | 'featured';

const DigitalProductPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    products,
    loading,
    total,
    pagination,
  } = useSelector((state: RootState) => state.digitalProduct);

  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [filters, setFilters] = useState<PublicFilterDigitalProduct>({
    page: 1,
    limit: 12,
  });

  const loadMoreRef = useRef<HTMLDivElement>(null);

  const getEffectiveFilters = useCallback((tab: TabType, baseFilters: PublicFilterDigitalProduct) => {
    const queryFilters: PublicFilterDigitalProduct = { ...baseFilters };
    if (tab === 'digital') queryFilters.productType = 'digital';
    if (tab === 'service') queryFilters.productType = 'service';
    if (tab === 'featured') queryFilters.isFeatured = true;
    queryFilters.category = 'Services';

    return queryFilters;
  }, []);

  const fetchData = useCallback(
    (tab: TabType, queryFilters: PublicFilterDigitalProduct, isMore = false) => {
      const finalFilters = getEffectiveFilters(tab, queryFilters);
      if (isMore) {
        dispatch(getMoreDigitalProducts(finalFilters));
      } else {
        dispatch(getDigitalProducts(finalFilters));
      }
    },
    [dispatch, getEffectiveFilters]
  );

  useEffect(() => {
    fetchData(activeTab, { ...filters, page: 1 }, false);
  }, [activeTab, fetchData]); // Only refetch when tab changes or activeTab logic changes

  // Separate effect for filter changes (except page resets handled by tab change)
  useEffect(() => {
    if (filters.page === 1) {
       fetchData(activeTab, filters, false);
    } else {
       fetchData(activeTab, filters, true);
    }
  }, [filters, activeTab, fetchData]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as TabType);
    setFilters({ page: 1, limit: 12 });
  };

  const handleFilterChange = (newFilters: PublicFilterDigitalProduct) => {
    setFilters({ ...newFilters, page: 1 }); // Always reset to page 1 on filter change
  };

  const handleLoadMore = () => {
    if (products.length < total && !loading) {
      setFilters(prev => ({ ...prev, page: (prev.page || 1) + 1 }));
    }
  };

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (!total || total <= filters.limit!) return null;
    
    const totalPages = Math.ceil(total / (filters.limit || 12));
    const page = filters.page || 1;
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <ul className="pagination pagination-dark d-flex justify-content-center pagination-lg mt-4">
        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
          <button className="page-link" onClick={() => handlePageChange(1)} disabled={page === 1} type="button">
            <i className="material-symbols-rounded">keyboard_double_arrow_left</i>
          </button>
        </li>
        {pages.map((pageNum) => (
          <li key={pageNum} className={`page-item ${pageNum === page ? 'active' : ''}`}>
            <button className="page-link" onClick={() => handlePageChange(pageNum)} type="button">
              {pageNum}
            </button>
          </li>
        ))}
        <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
          <button className="page-link" onClick={() => handlePageChange(totalPages)} disabled={page === totalPages} type="button">
            <i className="material-symbols-rounded">keyboard_double_arrow_right</i>
          </button>
        </li>
      </ul>
    );
  };

  return (
    <>
      <style>{`
        .deals-filter-sidebar {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e0 #f7fafc;
        }
        .deals-filter-sidebar::-webkit-scrollbar {
          width: 6px;
        }
        .deals-filter-sidebar::-webkit-scrollbar-track {
          background: #f7fafc;
          border-radius: 10px;
        }
        .deals-filter-sidebar::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 10px;
        }
        .deals-filter-sidebar::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
        .load-more-container {
           min-height: 100px;
           display: flex;
           justify-content: center;
           align-items: center;
        }
      `}</style>
      <div className="display-max">
        <NavbarTwo />
        <DigitalProductCategories activeTab={activeTab} onTabChange={handleTabChange} />
        
        <div className="container w-95">
          <div className="d-flex mt-1">
            {/* Sidebar Filter */}
            <div
              className="border-end custom-wid-1 text-start w-15 position-sticky deals-filter-sidebar"
              style={{
                top: '20px',
                alignSelf: 'flex-start',
                height: 'calc(100vh - 120px)',
                overflowY: 'auto',
                overflowX: 'hidden',
                paddingRight: '8px',
                backgroundColor: '#ffffff',
                zIndex: 10,
              }}
            >
              <DigitalProductFilter onFilterChange={handleFilterChange} currentFilters={filters} />
            </div>

            {/* Product Grid */}
            <div className="container mt-3 flex-grow-1">
              <div className="mb-5">
                <DigitalProducts
                  products={products}
                  loading={loading && filters.page === 1}
                  setSelectedProduct={setSelectedProduct}
                />
                
                {/* Lazy Loading / Load More Trigger */}
                {products.length < total && (
                  <div className="load-more-container mt-4">
                    <button 
                      className="btn btn-outline-primary btn-round" 
                      onClick={handleLoadMore}
                      disabled={loading}
                    >
                      {loading ? (
                        <><span className="spinner-border spinner-border-sm me-2" /> Loading...</>
                      ) : (
                        'Load More Products'
                      )}
                    </button>
                  </div>
                )}
              </div>
              
              {/* Traditional Pagination */}
              {renderPagination()}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="display-mini display-mob">
        <div className="d-flex justify-content-between align-items-center p-2 shadow-card bg-white position-fixed w-100 z-index-3">
          <div>
            <li className="avatar avatar-sm border-radius-2xl bg-gray-100">
              <a href="/HomePage">
                <i className="ri-arrow-left-s-line fs-3 me-1 text-black"></i>
              </a>
            </li>
          </div>
          <div className="d-flex gap-2">
            <li className="avatar avatar-sm border-radius-2xl bg-gray-100">
              <a href=".">
                <i className="bi bi-search fs-6 text-black"></i>
              </a>
            </li>
            <li className="avatar avatar-sm border-radius-2xl bg-gray-100">
              <a href=".">
                <i className="bi bi-bell fs-5 text-black"></i>
              </a>
            </li>
          </div>
        </div>

        <div className="pt-5">
          <div className="d-flex justify-content-between align-items-center border-bottom">
            <div className="w-80 border-end">
              <ul className="nav nav-pills gap-2 p-2 z-index-1 overflow-sides d-flex">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'digital', label: 'Digital' },
                  { id: 'service', label: 'Services' },
                  { id: 'featured', label: 'Featured' },
                ].map((tab) => (
                  <li key={tab.id} className="nav-item">
                    <button
                      className={`nav-link text-nowrap border-black ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => handleTabChange(tab.id)}
                      type="button"
                    >
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-20 text-center">
              <b
                className="text-black w-100 fs-6 fw-bold"
                data-bs-toggle="offcanvas"
                data-bs-target="#offcanvasFilters"
              >
                <i className="bi bi-sort-alpha-up me-1"></i>Filters
              </b>
            </div>
          </div>

          <div className="p-2">
            <DigitalProducts
              products={products}
              loading={loading && filters.page === 1}
              setSelectedProduct={setSelectedProduct}
            />
            
            {products.length < total && (
              <div className="text-center mt-4 mb-5">
                <button 
                  className="btn btn-sm btn-dark w-100" 
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Filter Offcanvas */}
        <div
          className="offcanvas offcanvas-bottom bg-purple-subtle min-vh-90"
          id="offcanvasFilters"
          aria-labelledby="offcanvasFiltersLabel"
        >
          <div className="d-flex justify-content-between align-items-start">
            <div className="text-end w-70"></div>
            <div>
              <button className="btn text-reset" data-bs-dismiss="offcanvas" type="button">
                <li className="avatar avatar-sm border shadow-lg border-radius-2xl opacity-6 bg-white">
                  <i className="bi bi-x-lg fs-6 text-black"></i>
                </li>
              </button>
            </div>
          </div>
          <div className="offcanvas-body border-top small">
            <div className="p-2">
              <DigitalProductFilter onFilterChange={handleFilterChange} currentFilters={filters} />
            </div>
          </div>
          <div className="px-2 mb-2">
            <button className="btn btn-md btn-dark w-100" data-bs-dismiss="offcanvas" type="button">
              Apply Filters
            </button>
          </div>
        </div>
      </div>
      <ProDetailModal product={selectedProduct} />
    </>
  );
};

export default DigitalProductPage;
